/**
 * React Query hooks for tenant-visible security alerts (Sprint 129 Stream F).
 *
 * Backend: ``GET /api/v1/tenants/{tenant_id}/security/alerts`` and
 * ``POST /api/v1/tenants/{tenant_id}/security/alerts/{id}/acknowledge``.
 * Cross-tenant isolation is enforced server-side; we still scope the
 * React Query cache by ``tenantId`` to avoid bleed across tenant switches.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  TenantSecurityAlert,
  TenantSecurityAlertList,
  TenantSecurityAlertSummary,
} from '../types';

const keys = {
  all: (tenantId?: string) => ['tenant-security-alerts', tenantId] as const,
  list: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...keys.all(tenantId), 'list', params] as const,
  summary: (tenantId?: string) =>
    [...keys.all(tenantId), 'active-summary'] as const,
};

function basePath(tenantId: string): string {
  return `/api/v1/tenants/${tenantId}/security`;
}

export function useTenantSecurityAlerts(
  params: { status?: string; severity?: string; limit?: number } = {},
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.list(tenantId, params),
    queryFn: async () => {
      const { data } = await apiClient.get<TenantSecurityAlertList>(
        `${basePath(tenantId!)}/alerts`,
        { params },
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

export function useTenantSecurityAlertSummary() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.summary(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<TenantSecurityAlertSummary>(
        `${basePath(tenantId!)}/alerts/active-summary`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (alertId: string): Promise<TenantSecurityAlert> => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<TenantSecurityAlert>(
        `${basePath(tenantId)}/alerts/${alertId}/acknowledge`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all(tenantId) });
    },
  });
}
